import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import TextInput from "components/ui/TextInput";
import EmailField from "components/muyeon/EmailField";
import CommonModal from "components/ui/CommonModal";
import { isTaskResumeRedirect, safeRedirect } from "util/pendingPosting";
import { pixelTrack } from "util/metaPixel";
import WebRoleOnboarding from "components/muyeon/WebRoleOnboarding";
import { openNativeOrWeb } from "common/nativeRoute";
import { openRoleOnboarding } from "common/nativeBridge";
import { popSignupAgreements, stashSignupAgreements, isAllRequired } from "util/consent";
import {
  callSendEmailCode,
  callVerifyEmailCode,
  callNiceIdentityConfig,
  callGetNiceIdentityResult,
} from "api/MuyeonApi";
import {
  startIdentityVerification,
  popIdentityForm,
  NICE_TOKEN_PARAM,
  NICE_ERROR_PARAM,
  NICE_ERROR_MESSAGE,
} from "util/niceIdentity";
import { trackClick } from "util/appAnalytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 무용연 회원가입 — 후니드 Join DOM 구조 사용.
 * form-list 패딩은 `#join .content-wrap .tab-content-group .tab-content .form-list{padding:4rem 2rem}`
 * 에서 오므로, 반드시 tab-content-group > tab-content.is-show 안에 form-list 를 둔다.
 */
const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/home";

  const [f, setF] = useState({
    email: "", password: "", passwordConfirm: "", name: "", phone: "",
  });
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(false);
  const [webRole, setWebRole] = useState(false); // 웹 회원유형 선택 온보딩(네이티브 브릿지 없을 때)

  // 이메일 인증 상태
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [vmsg, setVmsg] = useState(""); // 인증 안내 메시지
  const [vmsgErr, setVmsgErr] = useState(false); // true=에러(빨강) / false=정상 안내(회색)

  // 휴대폰 본인확인(NICE) 상태
  //  available=키가 설정돼 화면에 띄울 수 있는가 / required=없으면 가입을 막는가.
  //  둘 다 서버가 정한다(GET /identity/nice/config) — 키 발급 전에도 화면이 깨지지 않게.
  const [idAvailable, setIdAvailable] = useState(false);
  const [idRequired, setIdRequired] = useState(false);
  const [identity, setIdentity] = useState(null); // { token, name, phone(마스킹) }
  const [idBusy, setIdBusy] = useState(false);
  const [idMsg, setIdMsg] = useState("");

  const on = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e.value });

  useEffect(() => {
    callNiceIdentityConfig()
      .then(({ data }) => {
        setIdAvailable(!!data?.available);
        setIdRequired(!!data?.required);
      })
      .catch(() => {}); // 조회 실패 = 미사용으로 둔다(가입 자체를 막지 않는다)
  }, []);

  // 표준창에서 돌아왔을 때: 떠나기 전 폼을 되살리고 인증 결과를 붙인다.
  //  ⚠️ 비밀번호는 저장하지 않으므로 복원되지 않는다(util/niceIdentity 주석 참고).
  useEffect(() => {
    const token = params.get(NICE_TOKEN_PARAM);
    const err = params.get(NICE_ERROR_PARAM);

    // ★ 2026-10-19 결과 없이 돌아온 경우(인증 화면에서 뒤로가기·앱 '돌아가기')에도 떠나기 전 입력을 되살린다.
    //   종전엔 결과 쿼리가 있을 때만 되살려, 이메일 인증부터 가입을 다시 해야 했다.
    const saved = popIdentityForm();
    if (saved) {
      setF((prev) => ({ ...prev, ...saved.form }));
      if (saved.emailVerified) {
        setEmailVerified(true);
        setCodeSent(true);
      }
    }
    if (!token && !err) return;
    // 쿼리를 지워 새로고침 때 같은 토큰으로 다시 조회하지 않게 한다.
    window.history.replaceState(null, "", window.location.pathname + (redirect !== "/home" ? `?redirect=${encodeURIComponent(redirect)}` : ""));

    if (err) {
      setIdMsg(NICE_ERROR_MESSAGE[err] || NICE_ERROR_MESSAGE.FAILED);
      return;
    }
    callGetNiceIdentityResult(token)
      .then(({ data }) => {
        setIdentity({ token, name: data?.name || "", phone: data?.phone || "" });
        // 이름만 폼에 반영한다. 휴대폰은 **마스킹된 표시용 값**이라 폼에 넣으면 안 된다
        //  (그대로 제출되면 010-****-5678 이 저장되려 한다). 실제 번호는 서버가 토큰에서 꺼내 쓴다.
        setF((prev) => ({ ...prev, name: data?.name || prev.name }));
        setIdMsg("");
      })
      .catch((e) => setIdMsg(e?.response?.data?.message || "본인인증 결과를 확인하지 못했어요."));
    // params/redirect 는 최초 진입 시점 값이면 충분하다(복귀는 전체 새로고침이라 마운트가 다시 돈다).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyIdentity = useCallback(async () => {
    if (idBusy) return;
    setIdBusy(true);
    setIdMsg("");
    try {
      await startIdentityVerification({
        returnTo: `/register${redirect !== "/home" ? `?redirect=${encodeURIComponent(redirect)}` : ""}`,
        form: {
          form: { email: f.email, name: f.name },
          emailVerified,
        },
      });
      // 성공하면 페이지가 표준창으로 넘어가므로 여기 아래는 실행되지 않는다.
    } catch (e) {
      setIdMsg(e?.response?.data?.message || e?.message || "본인인증을 시작하지 못했어요.");
      setIdBusy(false);
    }
  }, [idBusy, redirect, f.email, f.name, emailVerified]);

  // 이메일이 바뀌면 인증 상태 초기화(다른 이메일로 인증 통과 방지).
  const onEmail = (e) => {
    const value = e?.target ? e.target.value : e.value;
    setF({ ...f, email: value });
    setEmailVerified(false);
    setCodeSent(false);
    setCode("");
    setVmsg("");
  };

  const showErr = (m) => { setVmsg(m); setVmsgErr(true); };
  const showInfo = (m) => { setVmsg(m); setVmsgErr(false); };

  const sendCode = async () => {
    if (!EMAIL_RE.test(f.email)) {
      showErr("올바른 이메일 주소를 입력해 주세요.");
      return;
    }
    setSending(true);
    setVmsg("");
    try {
      await callSendEmailCode({ email: f.email, purpose: "SIGNUP" });
      setCodeSent(true);
      showInfo("인증번호를 보냈어요. 메일함과 스팸함을 확인해 주세요. 인증번호는 10분 동안 쓸 수 있어요.");
    } catch (err) {
      showErr(err?.response?.data?.message || "인증번호 발송에 실패했어요.");
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    if (!code || code.length < 6) {
      showErr("인증번호 6자리를 입력해 주세요.");
      return;
    }
    setVerifying(true);
    try {
      await callVerifyEmailCode({ email: f.email, code, purpose: "SIGNUP" });
      setEmailVerified(true);
      setVmsg("");
    } catch (err) {
      showErr(err?.response?.data?.message || "인증번호가 일치하지 않아요.");
    } finally {
      setVerifying(false);
    }
  };

  // ★ 2026-10-22 가입 버튼이 막힌 사유 기록 — 광고 가입자가 버튼을 6번 누르고서야 가입됐다(어디서 막혔는지 보려고).
  const blocked = (reason) => trackClick("cta:signup_blocked", { props: { reason } });
  const submit = async () => {
    trackClick("cta:signup_submit");
    // 다중역할 개편: 가입 시 역할 선택 없음(일반회원). 역할은 MY에서 추가.
    if (!f.email || !f.password || !f.name) {
      setMsg("이메일, 비밀번호, 이름을 모두 입력해 주세요."); blocked("missing_fields");
      return;
    }
    if (!emailVerified) {
      setMsg("이메일 인증을 완료해 주세요."); blocked("email_unverified");
      return;
    }
    if (idRequired && !identity) {
      setMsg("휴대폰 본인인증을 완료해 주세요."); blocked("identity_missing");
      return;
    }
    if (f.password.length < 8) {
      setMsg("비밀번호는 8자 이상이어야 해요."); blocked("password_short");
      return;
    }
    if (f.password !== f.passwordConfirm) {
      setMsg("비밀번호가 일치하지 않아요."); blocked("password_mismatch");
      return;
    }
    // 동의화면에서 캐리된 약관 동의값(없으면 게이트가 재요청). 백엔드는 필수 동의 없으면 가입 거부.
    const agreements = popSignupAgreements();
    if (!agreements || !isAllRequired(agreements)) {
      setMsg("약관에 동의해야 가입할 수 있어요. 처음부터 다시 시작해 주세요."); blocked("terms_missing");
      return;
    }
    try {
      setMsg("");
      // 닉네임은 더 이상 화면에 노출되지 않아(표시명=본인확인 실명) 입력받지 않는다.
      //  서버가 미전달 시 이름으로 채운다(auth.service.register).
      await register({
        email: f.email,
        password: f.password,
        name: f.name,
        phone: identity ? undefined : (f.phone || undefined),
        // 본인확인 토큰. 서버가 1회 소비하며 이름·휴대폰을 인증 결과로 덮어쓴다.
        identityToken: identity?.token,
        agreements,
      });
      pixelTrack("CompleteRegistration", { content_name: "email" }); // 광고 전환(Meta) — 픽셀 ID 없으면 무시
      setDone(true); // 성공 안내 모달 → 확인 시 이동
    } catch (e) {
      stashSignupAgreements(agreements); // 재시도 위해 동의값 복원
      const m = e?.response?.data?.message;
      setMsg(Array.isArray(m) ? m.join("\n") : m || "회원가입에 실패했어요.");
    }
  };

  return (
    <div className="container">
      <div className="top-bar">
        <button type="button" className="ico-btn back" onClick={() => navigate(-1)}>
          <i className="arrow" />
          <span className="visually-hidden">뒤로가기</span>
        </button>
        <h2 className="page-name">회원가입</h2>
      </div>

      <div id="join" className="page">
        <div className="content-wrap">
          <p className="page-tit">가입에 필요한 정보를 입력해 주세요</p>

          <div className="tab-content-group">
            <div className="tab-content is-show">
              <ul className="main form-list">
                <li className="list-item">
                  {/* 도메인은 고르게 한다 — naver.con 같은 오타가 반송으로 이어지는 걸 막기 위해. */}
                  <EmailField label="이메일" required value={f.email}
                    onChange={(v) => onEmail({ value: v })} isDisabled={emailVerified} />
                </li>
                {emailVerified ? (
                  <li className="list-item">
                    <p className="t-14-single" style={{ color: "#F58232", fontWeight: 700 }}>
                      ✓ 인증 완료
                    </p>
                  </li>
                ) : (
                  <li className="list-item">
                    <button type="button" className="btn-md outlined" onClick={sendCode}
                      disabled={sending}>
                      {sending ? "전송 중…" : codeSent ? "인증번호 재전송" : "인증번호 받기"}
                    </button>
                    {codeSent && (
                      <div style={{ marginTop: "1.2rem" }}>
                        <TextInput label="인증번호" name="code" type="text" isValid
                          value={code}
                          onChange={(e) => setCode((e?.target ? e.target.value : e.value).replace(/\D/g, ""))}
                          placeholder="6자리 숫자" maxLength={6} />
                        <button type="button" className="btn-md filled primary" onClick={verifyCode}
                          disabled={verifying} style={{ marginTop: "0.8rem" }}>
                          {verifying ? "확인 중…" : "인증 확인"}
                        </button>
                      </div>
                    )}
                    {vmsg && (
                      <p className="t-14-single" style={{ color: vmsgErr ? "#e2362d" : "#6D6E71", marginTop: "0.8rem" }}>{vmsg}</p>
                    )}
                  </li>
                )}
                {/* 휴대폰 본인확인 — 비밀번호보다 앞에 둔다. 표준창은 전체 페이지가 넘어갔다
                    돌아오는데, 비밀번호는 저장하지 않으므로 뒤에 두면 매번 다시 쳐야 한다. */}
                {idAvailable && (
                  identity ? (
                    <li className="list-item">
                      <p className="t-14-single" style={{ color: "#F58232", fontWeight: 700 }}>
                        ✓ 본인인증 완료
                      </p>
                    </li>
                  ) : (
                    <li className="list-item">
                      <button type="button" className="btn-md outlined" onClick={verifyIdentity}
                        disabled={idBusy}>
                        {idBusy ? "이동 중…" : "휴대폰 본인인증"}
                        {idRequired ? " (필수)" : ""}
                      </button>
                      <p className="t-14-single" style={{ color: "#6D6E71", marginTop: "0.8rem" }}>
                        본인인증 창으로 이동해요. 확인이 끝나면 이 화면으로 돌아와요.
                      </p>
                      {idMsg && (
                        <p className="t-14-single" style={{ color: "#e2362d", marginTop: "0.8rem" }}>{idMsg}</p>
                      )}
                    </li>
                  )
                )}
                <li className="list-item">
                  <TextInput label="비밀번호" name="password" type="password" required isValid
                    value={f.password} onChange={on("password")} placeholder="8자 이상" maxLength={50} />
                </li>
                <li className="list-item">
                  <TextInput label="비밀번호 확인" name="passwordConfirm" type="password" required isValid
                    value={f.passwordConfirm} onChange={on("passwordConfirm")} placeholder="비밀번호 재입력" maxLength={50} />
                </li>
                {/* 본인확인을 마쳤으면 이름은 인증 결과가 정본이다(서버도 인증값으로 덮어쓴다). */}
                <li className="list-item">
                  <TextInput label="이름" name="name" required isValid
                    value={f.name} onChange={on("name")} placeholder="이름" maxLength={30}
                    isDisabled={!!identity} />
                </li>

                {/* 본인확인을 했으면 번호를 따로 받지 않는다 — 인증된 번호가 정본이다. */}
                {!identity && (
                  <li className="list-item">
                    <TextInput label="휴대폰 번호" name="phone" isValid
                      value={f.phone} onChange={on("phone")} placeholder="'-' 없이 숫자만" maxLength={20} />
                  </li>
                )}
              </ul>

              {msg && (
                <p className="t-14-single" style={{ color: "#e2362d", padding: "0 2rem" }}>{msg}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="tab-bar-btn-group fixed">
        <div className="btn-group">
          <button type="button" className="btn-md filled primary" onClick={submit}>가입하기</button>
        </div>
      </div>

      <CommonModal
        isShow={done}
        closeModal={() => {
          setDone(false);
          // 광고 유입 공고 작성 중 가입 → 활동 선택 없이 바로 돌아가 공고부터 등록(util/pendingPosting).
          if (isTaskResumeRedirect(redirect)) { // 광고 공고·레슨 요청 중 가입 → 유형 선택 없이 하던 일로
            navigate(safeRedirect(redirect), { replace: true });
            return;
          }
          // 네이티브: 회원유형 선택 온보딩(풀커버) → 선택 시 유형별 화면 전환(window.__onRoleSelected).
          // 웹: 대체 라우트가 없으므로(매핑 테이블 null) 웹 전용 회원유형 선택 모달로 폴백.
          openNativeOrWeb(openRoleOnboarding, {}, navigate, { onUnavailable: () => setWebRole(true) });
        }}
        content={{ title: "회원가입이 완료됐어요.", body: null }}
      />

      <WebRoleOnboarding isShow={webRole} redirect={redirect} />
    </div>
  );
};

export default Register;
